import React, { useState, useEffect } from 'react';
// import { DeleteButton } from './DeleteButton';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { LaTeXRenderer } from '@/components/LaTeXRenderer';
import { 
  HelpCircle, 
  MessageSquare, 
  ThumbsUp, 
  Send, 
  Users, 
  CheckCircle,
  Loader2,
  Plus,
  User,
  Reply,
  Star
} from 'lucide-react';

interface FAQDiscussion {
  id: string;
  question: string;
  answer: string | null;
  is_answered: boolean;
  anonymous_name: string;
  upvotes: number;
  created_at: string;
  updated_at: string;
}

interface StudentResponse {
  id: string;
  question_id: string;
  user_id: string;
  response: string;
  anonymous_name: string;
  upvotes: number;
  is_helpful: boolean;
  created_at: string;
}

export const FAQForum: React.FC = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [discussions, setDiscussions] = useState<FAQDiscussion[]>([]);
  const [studentResponses, setStudentResponses] = useState<StudentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answering, setAnswering] = useState<string | null>(null);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [showNewQuestion, setShowNewQuestion] = useState(false);

  const [newQuestion, setNewQuestion] = useState({
    question: '',
    anonymous_name: '',
    image: null as File | null
  });

  const [newAnswer, setNewAnswer] = useState('');
  const [newStudentResponse, setNewStudentResponse] = useState({
    response: '',
    anonymous_name: '',
    image: null as File | null
  });

  const isTeacher = profile?.role === 'teacher';
  const isMembre = profile?.subscription_type === 'membre' || profile?.subscription_type === 'premium';
  
  // Vérification d'accès : Membre ou Enseignant requis
  if (!isMembre && !isTeacher) {
    return (
      <div className="text-center py-12">
        <HelpCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-secondary mb-4">
          Forum FAQ Membre
        </h2>
        <p className="text-muted-foreground mb-6">
          L'accès au forum FAQ est réservé aux membres Membre
        </p>
        <Badge className="sprintbac-gradient text-white">
          Accès Membre Requis
        </Badge>
      </div>
    );
  }

  // Function to render text with LaTeX support
  const renderTextWithLatex = (text: string) => {
    const parts = text.split(/(\\$\\$[^$]+\\$\\$|\\$[^$]+\\$)/);
    
    return parts.map((part, index) => {
      if (part.startsWith('$$') && part.endsWith('$$')) {
        const latex = part.slice(2, -2);
        return <LaTeXRenderer key={index} content={latex} inline={false} />;
      } else if (part.startsWith('$') && part.endsWith('$')) {
        const latex = part.slice(1, -1);
        return <LaTeXRenderer key={index} content={latex} inline={true} />;
      } else {
        return <span key={index}>{part}</span>;
      }
    });
  };

  const loadDiscussions = async () => {
    try {
      setLoading(true);
      
      const [discussionsResult, responsesResult] = await Promise.all([
        supabase
          .from('faq_discussions_2025_12_10_10_05')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('faq_student_responses_2025_12_10_13_10')
          .select('*')
          .order('created_at', { ascending: true })
      ]);

      if (discussionsResult.error) throw discussionsResult.error;
      if (responsesResult.error) throw responsesResult.error;

      setDiscussions(discussionsResult.data || []);
      setStudentResponses(responsesResult.data || []);
    } catch (error: any) {
      console.error('Error loading discussions:', error);
      toast({
        title: "❌ Erreur de chargement",
        description: "Impossible de charger les discussions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const submitQuestion = async () => {
    if (!newQuestion.question.trim()) return;

    setSubmitting(true);
    try {
      // Upload image si présente
      let imageUrl = null;
      if (newQuestion.image) {
        imageUrl = await uploadImage(newQuestion.image);
      }

      const { error } = await supabase
        .from('faq_discussions_2025_12_10_10_05')
        .insert([{
          question: newQuestion.question,
          anonymous_name: newQuestion.anonymous_name || 'Étudiant Anonyme',
          image_url: imageUrl
        }]);

      if (error) throw error;

      toast({
        title: "✅ Question posée",
        description: "Votre question a été ajoutée au forum",
      });

      setNewQuestion({ question: '', anonymous_name: '' });
      setShowNewQuestion(false);
      loadDiscussions();
    } catch (error: any) {
      toast({
        title: "❌ Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitTeacherAnswer = async (discussionId: string) => {
    if (!newAnswer.trim()) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('faq_discussions_2025_12_10_10_05')
        .update({
          answer: newAnswer,
          is_answered: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', discussionId);

      if (error) throw error;

      toast({
        title: "✅ Réponse ajoutée",
        description: "Votre réponse a été publiée",
      });

      setNewAnswer('');
      setAnswering(null);
      loadDiscussions();
    } catch (error: any) {
      toast({
        title: "❌ Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction d'upload d'image
  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `forum/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('forum-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('forum-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Erreur upload image:', error);
      return null;
    }
  };

  const submitStudentResponse = async (questionId: string) => {
    if (!newStudentResponse.response.trim()) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('faq_student_responses_2025_12_10_13_10')
        .insert([{
          question_id: questionId,
          user_id: profile?.id,
          response: newStudentResponse.response,
          anonymous_name: newStudentResponse.anonymous_name || 'Étudiant Anonyme'
        }]);

      if (error) throw error;

      toast({
        title: "✅ Réponse ajoutée",
        description: "Votre réponse a été publiée",
      });

      setNewStudentResponse({ response: '', anonymous_name: '' });
      setRespondingTo(null);
      loadDiscussions();
    } catch (error: any) {
      toast({
        title: "❌ Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const upvoteQuestion = async (discussionId: string, currentUpvotes: number) => {
    try {
      const { error } = await supabase
        .from('faq_discussions_2025_12_10_10_05')
        .update({ upvotes: currentUpvotes + 1 })
        .eq('id', discussionId);

      if (error) throw error;

      setDiscussions(prev => 
        prev.map(d => 
          d.id === discussionId 
            ? { ...d, upvotes: d.upvotes + 1 }
            : d
        )
      );

      toast({
        title: "👍 Vote ajouté",
        description: "Merci pour votre vote !",
      });
    } catch (error: any) {
      toast({
        title: "❌ Erreur",
        description: "Impossible d'ajouter le vote",
        variant: "destructive",
      });
    }
  };

  const upvoteResponse = async (responseId: string, currentUpvotes: number) => {
    try {
      const { error } = await supabase
        .from('faq_student_responses_2025_12_10_13_10')
        .update({ upvotes: currentUpvotes + 1 })
        .eq('id', responseId);

      if (error) throw error;

      setStudentResponses(prev => 
        prev.map(r => 
          r.id === responseId 
            ? { ...r, upvotes: r.upvotes + 1 }
            : r
        )
      );

      toast({
        title: "👍 Vote ajouté",
        description: "Merci pour votre vote !",
      });
    } catch (error: any) {
      toast({
        title: "❌ Erreur",
        description: "Impossible d'ajouter le vote",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadDiscussions();
  }, []);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <HelpCircle className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold text-secondary">
            Forum FAQ avec Support LaTeX
          </h1>
          <MessageSquare className="h-6 w-6 text-primary" />
        </div>
        <p className="text-muted-foreground">
          Posez vos questions avec formules mathématiques et aidez la communauté
        </p>
      </div>

      {/* New Question Button */}
      <div className="text-center">
        <Button
          onClick={() => setShowNewQuestion(!showNewQuestion)}
          className="sprintbac-gradient hover:opacity-90"
          size="lg"
        >
          <Plus className="mr-2 h-5 w-5" />
          Poser une question (avec LaTeX)
        </Button>
      </div>

      {/* New Question Form */}
      {showNewQuestion && (
        <Card className="border-0 sprintbac-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Nouvelle question avec support LaTeX
            </CardTitle>
            <CardDescription>
              Utilisez $ pour les formules inline et $$ pour les formules en bloc
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Nom d'affichage (optionnel)
              </label>
              <Input
                placeholder="Étudiant Anonyme"
                value={newQuestion.anonymous_name}
                onChange={(e) => setNewQuestion(prev => ({ ...prev, anonymous_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Votre question (avec LaTeX si nécessaire)
              </label>
              <Textarea
                placeholder="Exemple: Comment résoudre $x^2 + 5x + 6 = 0$ ?"
                value={newQuestion.question}
                onChange={(e) => setNewQuestion(prev => ({ ...prev, question: e.target.value }))}
                rows={4}
              />
              {newQuestion.question && (
                <div className="mt-2 p-3 bg-gray-50 rounded border">
                  <p className="text-sm font-medium mb-2">Aperçu :</p>
                  <div>{renderTextWithLatex(newQuestion.question)}</div>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Ajouter une image (optionnel)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setNewQuestion(prev => ({ ...prev, image: file }));
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={submitQuestion}
                disabled={submitting || !newQuestion.question.trim()}
                className="sprintbac-gradient hover:opacity-90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Publication...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Publier la question
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setShowNewQuestion(false);
                  setNewQuestion({ question: '', anonymous_name: '' });
                }}
                variant="outline"
              >
                Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 sprintbac-shadow">
          <CardContent className="pt-6 text-center">
            <HelpCircle className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary">{discussions.length}</div>
            <p className="text-sm text-muted-foreground">Questions posées</p>
          </CardContent>
        </Card>
        <Card className="border-0 sprintbac-shadow">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary">
              {discussions.filter(d => d.is_answered).length}
            </div>
            <p className="text-sm text-muted-foreground">Réponses prof</p>
          </CardContent>
        </Card>
        <Card className="border-0 sprintbac-shadow">
          <CardContent className="pt-6 text-center">
            <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary">{studentResponses.length}</div>
            <p className="text-sm text-muted-foreground">Réponses étudiants</p>
          </CardContent>
        </Card>
        <Card className="border-0 sprintbac-shadow">
          <CardContent className="pt-6 text-center">
            <ThumbsUp className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary">
              {discussions.reduce((sum, d) => sum + d.upvotes, 0) + 
               studentResponses.reduce((sum, r) => sum + r.upvotes, 0)}
            </div>
            <p className="text-sm text-muted-foreground">Votes positifs</p>
          </CardContent>
        </Card>
      </div>

      {/* Discussions List */}
      <Card className="border-0 sprintbac-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Discussions de la communauté
          </CardTitle>
          <CardDescription>
            Questions et réponses avec support LaTeX complet
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : discussions.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucune question pour le moment</p>
              <p className="text-sm text-muted-foreground mt-2">
                Soyez le premier à poser une question avec LaTeX !
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {discussions.map((discussion) => {
                const responses = studentResponses.filter(r => r.question_id === discussion.id);
                
                return (
                  <div
                    key={discussion.id}
                    className="border rounded-lg p-6 bg-gradient-to-r from-white to-blue-50"
                  >
                    {/* Question */}
                    <div className="mb-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-secondary">{discussion.anonymous_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(discussion.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={discussion.is_answered ? "default" : "secondary"}>
                            {discussion.is_answered ? (
                              <>
                                <CheckCircle className="mr-1 h-3 w-3" />
                                Répondu
                              </>
                            ) : (
                              'En attente'
                            )}
                          </Badge>
                          <Button
                            onClick={() => upvoteQuestion(discussion.id, discussion.upvotes)}
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <ThumbsUp className="h-3 w-3" />
                            {discussion.upvotes}
                          </Button>
                          {isTeacher && (
                            <button
                              onClick={() => {
                                if (window.confirm('Supprimer cette question ?')) {
                                  supabase.from('faq_discussions_2025_12_10_10_05').delete().eq('id', discussion.id).then(() => loadData());
                                }
                              }}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-secondary font-medium mb-2">Question :</p>
                      <div className="text-muted-foreground bg-white p-3 rounded border">
                        {renderTextWithLatex(discussion.question)}
                        {discussion.image_url && (
                          <img 
                            src={discussion.image_url} 
                            alt="Image question" 
                            className="mt-3 max-w-md rounded border"
                            style={{maxHeight: '300px', objectFit: 'contain'}}
                          />
                        )}
                      </div>
                    </div>

                    {/* Teacher Answer */}
                    {discussion.is_answered && discussion.answer ? (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
                        <p className="text-green-800 font-medium mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          Réponse de l'enseignant :
                        </p>
                        <div className="text-green-700">
                          {renderTextWithLatex(discussion.answer)}
                        </div>
                      </div>
                    ) : isTeacher ? (
                      <div className="space-y-3 mb-4">
                        {answering === discussion.id ? (
                          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <Textarea
                              placeholder="Tapez votre réponse ici (LaTeX supporté avec $ et $$)..."
                              value={newAnswer}
                              onChange={(e) => setNewAnswer(e.target.value)}
                              rows={3}
                              className="mb-3"
                            />
                            {newAnswer && (
                              <div className="mb-3 p-3 bg-white rounded border">
                                <p className="text-sm font-medium mb-2">Aperçu :</p>
                                <div>{renderTextWithLatex(newAnswer)}</div>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button
                                onClick={() => submitTeacherAnswer(discussion.id)}
                                disabled={submitting || !newAnswer.trim()}
                                size="sm"
                                className="sprintbac-gradient hover:opacity-90"
                              >
                                {submitting ? (
                                  <>
                                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                    Publication...
                                  </>
                                ) : (
                                  <>
                                    <Send className="mr-2 h-3 w-3" />
                                    Publier la réponse
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={() => {
                                  setAnswering(null);
                                  setNewAnswer('');
                                }}
                                variant="outline"
                                size="sm"
                              >
                                Annuler
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            onClick={() => setAnswering(discussion.id)}
                            variant="outline"
                            size="sm"
                          >
                            <MessageSquare className="mr-2 h-3 w-3" />
                            Répondre (Enseignant)
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 rounded-lg border mb-4">
                        <p className="text-muted-foreground text-sm">
                          En attente de réponse d'un enseignant...
                        </p>
                      </div>
                    )}

                    {/* Student Responses */}
                    {responses.length > 0 && (
                      <div className="space-y-3 mb-4">
                        <h4 className="font-medium text-secondary flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Réponses de la communauté ({responses.length})
                        </h4>
                        {responses.map((response) => (
                          <div key={response.id} className="bg-blue-50 p-3 rounded border border-blue-200">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-blue-600" />
                                <span className="text-sm font-medium text-blue-800">
                                  {response.anonymous_name}
                                </span>
                                {response.is_helpful && (
                                  <Star className="h-3 w-3 text-yellow-500 fill-current" />
                                )}
                              </div>
                              <Button
                                onClick={() => upvoteResponse(response.id, response.upvotes)}
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2"
                              >
                                <ThumbsUp className="h-3 w-3 mr-1" />
                                {response.upvotes}
                              </Button>
                            </div>
                            <div className="text-blue-700 text-sm">
                              {renderTextWithLatex(response.response)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Student Response Form */}
                    <div className="space-y-3">
                      {respondingTo === discussion.id ? (
                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                          <div className="space-y-3">
                            <Input
                              placeholder="Votre nom (optionnel)"
                              value={newStudentResponse.anonymous_name}
                              onChange={(e) => setNewStudentResponse(prev => ({ ...prev, anonymous_name: e.target.value }))}
                            />
                            <Textarea
                              placeholder="Votre réponse (LaTeX supporté avec $ et $$)..."
                              value={newStudentResponse.response}
                              onChange={(e) => setNewStudentResponse(prev => ({ ...prev, response: e.target.value }))}
                              rows={3}
                            />
                            {newStudentResponse.response && (
                              <div className="p-3 bg-white rounded border">
                                <p className="text-sm font-medium mb-2">Aperçu :</p>
                                <div>{renderTextWithLatex(newStudentResponse.response)}</div>
                              </div>
                            )}
                            <div className="mb-3">
                              <label className="block text-sm font-medium mb-2">Ajouter une image (optionnel)</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  setNewStudentResponse(prev => ({ ...prev, image: file }));
                                }}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => submitStudentResponse(discussion.id)}
                                disabled={submitting || !newStudentResponse.response.trim()}
                                size="sm"
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                              >
                                {submitting ? (
                                  <>
                                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                    Publication...
                                  </>
                                ) : (
                                  <>
                                    <Send className="mr-2 h-3 w-3" />
                                    Publier ma réponse
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={() => {
                                  setRespondingTo(null);
                                  setNewStudentResponse({ response: '', anonymous_name: '' });
                                }}
                                variant="outline"
                                size="sm"
                              >
                                Annuler
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setRespondingTo(discussion.id)}
                          variant="outline"
                          size="sm"
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <Reply className="mr-2 h-3 w-3" />
                          Aider avec une réponse
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-0 bg-gradient-to-r from-blue-50 to-green-50">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="font-semibold text-secondary mb-2">
              💡 Forum FAQ avec LaTeX - Mode d'emploi
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div>
                <p><strong>Formules LaTeX :</strong></p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Inline: $x^2 + 1 = 0$</li>
                  <li>Bloc: {`$$\\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$$`}</li>
                  <li>Fractions, racines, intégrales supportées</li>
                </ul>
              </div>
              <div>
                <p><strong>Participation :</strong></p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Étudiants peuvent répondre aux questions</li>
                  <li>Votez pour les meilleures réponses</li>
                  <li>Enseignants donnent réponses officielles</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};